import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
// const API_BASE_URL = window.location.origin+'/api'
// const API_BASE_URL = ''

// Создаем отдельный экземпляр для длительных операций
const longTimeoutApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 600000, // 10 минут для загрузки файлов
});

// Создаем экземпляр для очень длительных операций (скачивание больших файлов)
const veryLongTimeoutApi = axios.create({
  baseURL: API_BASE_URL,
  timeout: 1800000, // 30 минут для скачивания больших файлов
});

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 60 секунд для обычных запросов
});

// Интерцептор для логирования
api.interceptors.request.use((config) => {
  console.log(`🚀 ${config.method?.toUpperCase()} ${config.url}`);
  return config;
});

// Добавляем токен авторизации для всех запросов
const addAuthInterceptor = (axiosInstance) => {
  axiosInstance.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
};

// Добавляем авторизацию во все экземпляры
addAuthInterceptor(api);
addAuthInterceptor(longTimeoutApi);
addAuthInterceptor(veryLongTimeoutApi);

longTimeoutApi.interceptors.request.use((config) => {
  console.log(`⏳ ${config.method?.toUpperCase()} ${config.url} (long timeout: ${config.timeout}ms)`);
  return config;
});

veryLongTimeoutApi.interceptors.request.use((config) => {
  console.log(`⏰ ${config.method?.toUpperCase()} ${config.url} (very long timeout: ${config.timeout}ms)`);
  return config;
});

// Интерцепторы для обработки ошибок
const addErrorInterceptors = (axiosInstance) => {
  axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.code === 'ECONNABORTED') {
        console.error('⏰ Таймаут запроса:', error.config?.url);
      } else if (error.response) {
        console.error('❌ Ошибка сервера:', error.response.status, error.response.data);
      } else if (error.request) {
        console.error('📡 Ошибка сети:', error.message);
      } else {
        console.error('❓ Неизвестная ошибка:', error.message);
      }
      return Promise.reject(error);
    }
  );
};

// Добавляем обработчики ошибок
addErrorInterceptors(api);
addErrorInterceptors(longTimeoutApi);
addErrorInterceptors(veryLongTimeoutApi);

export const medicalApi = {
  // Загрузка NIfTI файла с увеличенным таймаутом
  uploadNiiFile: async (file, threshold = 0.5) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('threshold', threshold.toString());
    formData.append('file_type', 'nii');
    
    const response = await longTimeoutApi.post('/process/remote/file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  // Загрузка MHA файла
  uploadMhaFile: async (file, threshold = 0.5) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('threshold', threshold.toString());
    formData.append('file_type', 'mha');
    
    const response = await longTimeoutApi.post('/process/remote/file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },

  // Загрузка DICOM ZIP файла
  uploadDicomZip: async (zipFile) => {
    const formData = new FormData();
    formData.append('zip_file', zipFile);
    
    console.log('📤 Отправка DICOM ZIP архива:', zipFile.name, 'размер:', (zipFile.size / 1024 / 1024).toFixed(2), 'MB');
    
    const response = await longTimeoutApi.post('/process/remote/dicom-zip', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`📤 Прогресс загрузки ZIP: ${percentCompleted}%`);
        }
      },
    });
    return response.data;
  },

  // Загрузка DICOM файлов напрямую (для совместимости)
  uploadDicomFolder: async (files) => {
    const formData = new FormData();
    
    console.log('📤 Отправка DICOM папки, файлов:', files.length);
    
    // Добавляем все файлы из папки
    files.forEach((file, index) => {
      formData.append('files', file);
      console.log(`   Файл ${index + 1}: ${file.name} (${(file.size / 1024 / 1024).toFixed(2)} MB)`);
    });
    
    const response = await longTimeoutApi.post('/process/remote/dicom', formData, {
      timeout: 300000, // 5 минут вместо стандартных 30 секунд
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`📤 Прогресс загрузки: ${percentCompleted}%`);
        }
      },
    });
    return response.data;
  },

  // Проверка статуса задачи
  getTaskStatus: async (taskId) => {
    console.log('📋 Проверка статуса задачи:', taskId);
    const response = await api.get(`/remote/status/${taskId}`);
    return response.data;
  },

  // Получение информации о результатах
  getResultsInfo: async (taskId) => {
    console.log('📋 Получение информации о результатах:', taskId);
    const response = await api.get(`/remote/results/${taskId}`);
    return response.data;
  },

  // Скачивание файла с очень большим таймаутом
  downloadFile: async (taskId, fileType) => {
    console.log('📥 Скачивание файла:', taskId, fileType);
    const response = await veryLongTimeoutApi.get(`/remote/download/${taskId}/${fileType}`, {
      responseType: 'blob',
      onDownloadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`📥 Прогресс скачивания: ${percentCompleted}%`);
        } else {
          console.log(`📥 Загружено: ${(progressEvent.loaded / 1024 / 1024).toFixed(2)} MB`);
        }
      },
    });
    return response.data;
  },

  // Проверка здоровья сервера
  healthCheck: async () => {
    try {
      console.log('🏥 Проверка здоровья сервера');
      const response = await api.get('/remote/health', { timeout: 10000 });
      return response.status === 200;
    } catch (error) {
      console.error('Сервер не доступен:', error.message);
      return false;
    }
  },

  // Отмена задачи
  cancelTask: async (taskId) => {
    console.log('⏹️ Отмена задачи:', taskId);
    const response = await api.post(`/remote/cancel/${taskId}`);
    return response.data;
  },

  // Сохранение 3D модели в историю (с авторизацией и загрузкой файла)
  saveToHistory: async (formData) => {
    console.log('💾 Сохранение модели в историю с загрузкой файла');
    const token = localStorage.getItem('token');
    const response = await longTimeoutApi.post('/history', formData, {
      headers: {
        'Authorization': token ? `Bearer ${token}` : '',
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Получение истории сканов (с авторизацией)
  getHistory: async () => {
    console.log('📋 Получение истории сканов');
    const response = await api.get('/history');
    return response.data;
  },
};

export default api;